import { ChevronDown } from "lucide-react"
import { useState } from "react"

import { cn } from "@/lib/utils"

import { Eyebrow } from "./eyebrow"

interface Item {
  question: string
  answer: string
}

/**
 * Absorbs the trust and control questions so the sections above don't have
 * to. Answers are template defaults, written plainly — partners will be able
 * to customise copy later.
 */
const ITEMS: Item[] = [
  {
    question: "Will it sound like a robot?",
    answer:
      "No. Your receptionist handles natural back-and-forth, pauses, and interruptions the way a real conversation flows.",
  },
  {
    question: "What happens when a caller needs a real person?",
    answer:
      "You set the rules. It can transfer calls to you or your team during the hours you choose, and outside them it takes a message and sends you the details straight away.",
  },
  {
    question: "Can I keep my phone number?",
    answer:
      "Yes. Keep your existing number and forward calls to your receptionist when you don't pick up, over the weekend, or full time if you prefer. You can adjust this at any time.",
  },
  {
    question: "How does the training work?",
    answer:
      "It starts by reading your website, then you can add details it should know, like services, prices, directions, and policies. You control what it says.",
  },
  {
    question: "What if it's asked something it doesn't know?",
    answer:
      "It says so honestly, then offers to help another way, like taking a message, booking a callback, or transferring to your team. It won't guess or make something up. If a topic keeps coming up, you can add it to its knowledge and it'll have the answer going forward.",
  },
  {
    question: "Is there a contract?",
    answer: "No. Billing is month to month, so you can cancel at any time.",
  },
]

export function Faq() {
  const [open, setOpen] = useState<number | null>(0)

  return (
    <section id="faq" className="w-full px-4 py-16 sm:px-8 sm:py-24">
      <div className="mx-auto w-full max-w-3xl">
        <div className="flex flex-col items-center text-center">
          <Eyebrow>FAQ</Eyebrow>
          <h2 className="font-lp-serif text-lp-fg mt-3 text-[clamp(1.625rem,2.8vw,2.125rem)] leading-[1.1] tracking-[-0.02em]">
            Common questions
          </h2>
        </div>

        <div className="divide-lp-border/70 border-lp-border/70 mt-12 divide-y border-y">
          {ITEMS.map((item, i) => {
            const isOpen = open === i
            return (
              <div key={item.question}>
                <button
                  type="button"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : i)}
                  className="flex w-full items-center justify-between gap-4 py-5 text-left"
                >
                  <span className="text-lp-fg text-base font-medium">
                    {item.question}
                  </span>
                  <ChevronDown
                    className={cn(
                      "text-lp-muted size-4 shrink-0 transition-transform duration-200",
                      isOpen && "rotate-180",
                    )}
                  />
                </button>
                <div
                  className={cn(
                    "grid transition-[grid-template-rows] duration-200 ease-out",
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
                  )}
                >
                  <div className="overflow-hidden">
                    <p className="text-lp-muted max-w-[62ch] pb-5 text-sm leading-[1.7]">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </section>
  )
}
